import type ParseType from 'parse';

import {
  MemberTopology,
  ObjectTopology,
  ParseBaseRepository,
  UserTopology,
  WorkspaceTopology,
} from '@telespot/infrastructure/parse';
import {
  type IWorkspaceMembershipRepository,
  WorkspaceMembershipDto,
} from '@telespot/management/use-cases';

export class ParseWorkspaceMembershipRepository
  extends ParseBaseRepository
  implements IWorkspaceMembershipRepository
{
  constructor(parse: typeof ParseType, options?: Parse.FullOptions) {
    super(parse, options);
  }

  public async list(
    workspaceId: string,
    limit: number,
    searchTerm?: string,
    lastMembershipId?: string
  ): Promise<WorkspaceMembershipDto[]> {
    const workspacePointer = this.subclasses
      .getSubclass(WorkspaceTopology.TABLE)
      .createWithoutData(workspaceId);

    const query = new this.parse.Query(MemberTopology.TABLE)
      .equalTo(MemberTopology.WORKSPACE, workspacePointer)
      .ascending(ObjectTopology.ID);

    if (searchTerm) {
      const userQuery = new this.parse.Query(this.parse.User).matches(
        UserTopology.USERNAME,
        new RegExp(this.escapeRegExp(searchTerm), 'i')
      );
      query.matchesQuery(MemberTopology.USER, userQuery);
    }

    if (lastMembershipId) query.greaterThan(ObjectTopology.ID, lastMembershipId);
    if (limit) query.limit(limit);

    query.include(MemberTopology.USER, MemberTopology.WORKSPACE);

    const rows = await query.find(this.options);
    return rows.map((row) => this.toDto(row, workspaceId));
  }

  private toDto(row: Parse.Object, workspaceId: string): WorkspaceMembershipDto {
    const userObj = row.get(MemberTopology.USER) as Parse.Object | undefined;
    const workspaceObj = row.get(MemberTopology.WORKSPACE) as Parse.Object | undefined;
    return {
      membershipId: row.id,
      workspaceId,
      workspaceName: workspaceObj?.get(WorkspaceTopology.NAME),
      userId: userObj?.id,
      username: userObj?.get(UserTopology.USERNAME),
      userVerified: userObj?.get(UserTopology.VERIFIED),
    };
  }
}
